import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { LiveKitRoom, RoomAudioRenderer, VideoTrack, useTracks, useParticipants, useRoomContext } from "@livekit/components-react";
import { Track, RoomEvent } from "livekit-client";
import { avatarUrl, setAvatarScope } from "@/lib/avatar";
import { isHostParticipant } from "@/lib/roles";
import { BrandAvatar } from "@/components/BrandAvatar";
import { UdyogLogo } from "@/components/UdyogLogo";
import { api } from "@/lib/api";

/**
 * The page LiveKit's cloud recorder loads (passed as custom_base_url). It joins
 * the room as a hidden participant and renders the branded class view, so a
 * recording is never a black screen just because nobody has their camera on.
 * Lifecycle is signalled to the recorder over console.log, per the egress spec.
 */
function Stage({ roomName }) {
  const room = useRoomContext();
  setAvatarScope(roomName);
  const participants = useParticipants();
  const screens = useTracks([Track.Source.ScreenShare], { onlySubscribed: true });
  const cams = useTracks([Track.Source.Camera], { onlySubscribed: true });
  const [pinned, setPinned] = useState(null);

  useEffect(() => {
    const start = () => console.log("START_RECORDING");
    const end = () => console.log("END_RECORDING");
    if (room.state === "connected") start();
    room.on("connected", start);
    room.on("disconnected", end);
    return () => {
      room.off("connected", start);
      room.off("disconnected", end);
    };
  }, [room]);

  // The host-wide pin decides what the recording shows. Read the persisted
  // value once, then stay in sync with live pin / unpin commands.
  useEffect(() => {
    if (!roomName) return;
    let cancelled = false;
    api
      .get(`/public/meetings/${roomName}`)
      .then(({ data }) => {
        if (!cancelled && data?.pinned_identity) setPinned(data.pinned_identity);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [roomName]);

  useEffect(() => {
    const onData = (payload) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (msg?.type !== "cmd") return;
        if (msg.cmd === "pin" && msg.identity) setPinned(msg.identity);
        else if (msg.cmd === "unpin") setPinned(null);
      } catch (_) {}
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => room.off(RoomEvent.DataReceived, onData);
  }, [room]);

  const share = screens[0];
  const camFor = (identity) => cams.find((t) => t.participant.identity === identity);
  const others = participants.filter((p) => !p.isLocal);
  const pinnedP = pinned ? others.find((p) => p.identity === pinned) : null;
  // Pinned → that person owns the whole frame (their camera, or their contact
  // card when the camera is off). Nothing published at all → the host's card.
  const hostOnly = pinnedP ? !camFor(pinnedP.identity) : !share && cams.length === 0;
  const people = pinnedP
    ? [pinnedP]
    : hostOnly
    ? others.filter(isHostParticipant).slice(0, 1)
    : others.filter((p) => camFor(p.identity)).slice(0, 9);
  const cols = people.length <= 1 ? 1 : people.length <= 4 ? 2 : 3;
  const showShare = !!share && !hostOnly;

  return (
    <div className="fixed inset-0 bg-[#050505] text-white overflow-hidden">
      {showShare ? (
        <VideoTrack trackRef={share} className="absolute inset-0 w-full h-full object-contain bg-black" />
      ) : hostOnly ? (
        <div className="absolute inset-0 grid place-items-center">
          {people[0] ? (
            <div className="text-center" data-testid="record-host-card">
              <div className="grid place-items-center mb-7">
                {isHostParticipant(people[0]) ? (
                  <BrandAvatar size={260} />
                ) : (
                  <img
                    src={avatarUrl(people[0].name || people[0].identity)}
                    alt=""
                    className="w-[260px] h-[260px] rounded-full object-cover ring-1 ring-emerald-500/40"
                  />
                )}
              </div>
              <div className="text-2xl tracking-wide uppercase text-white/85">
                {people[0].name || people[0].identity}
              </div>
            </div>
          ) : (
            <div className="text-center">
              <UdyogLogo size={54} className="justify-center mb-5" />
              <div className="text-white/35 text-lg">Class in progress</div>
            </div>
          )}
        </div>
      ) : (
        <div
          className="absolute inset-0 p-8 grid gap-5 place-content-center"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {people.map((p) => {
            const cam = camFor(p.identity);
            const name = p.name || p.identity;
            return (
              <div
                key={p.identity}
                className="relative rounded-3xl overflow-hidden bg-neutral-950 ring-1 ring-white/10 aspect-video grid place-items-center"
              >
                {cam ? (
                  <VideoTrack trackRef={cam} className="absolute inset-0 w-full h-full object-cover" />
                ) : isHostParticipant(p) ? (
                  <BrandAvatar size={people.length <= 2 ? 150 : 96} />
                ) : (
                  <img
                    src={avatarUrl(name)}
                    alt=""
                    className={`rounded-full ring-1 ring-emerald-500/40 object-cover ${
                      people.length <= 2 ? "w-40 h-40" : "w-24 h-24"
                    }`}
                  />
                )}
                <div className="absolute bottom-4 left-4 px-3 py-1 rounded-full bg-black/70 text-[13px] tracking-wide uppercase">
                  {name}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <RoomAudioRenderer />
      <div className="absolute top-6 left-6 opacity-80">
        <UdyogLogo size={26} />
      </div>
    </div>
  );
}

export default function RecordView() {
  const [params] = useSearchParams();
  const { roomName } = useParams();
  const serverUrl = params.get("url");
  const token = params.get("token");

  if (!serverUrl || !token) {
    return (
      <div className="fixed inset-0 bg-black text-white/40 grid place-items-center text-sm">
        This page is used by the recorder.
      </div>
    );
  }

  return (
    <LiveKitRoom
      serverUrl={serverUrl}
      token={token}
      connect
      audio={false}
      video={false}
      options={{ adaptiveStream: false, dynacast: false }}
    >
      <Stage roomName={roomName} />
    </LiveKitRoom>
  );
}
